import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { CheckCircle2, XCircle, ShieldOff, ShieldCheck, RefreshCcw, MoreHorizontal, Users, AlertTriangle } from "lucide-react";

const STATUS_FILTERS = [
  { key: "", label: "Todos" },
  { key: "pending", label: "Pendentes" },
  { key: "active", label: "Ativos" },
  { key: "blocked", label: "Bloqueados" },
  { key: "rejected", label: "Rejeitados" },
  { key: "archived", label: "Removidos" },
];

const PLANS = [
  { key: "mentor_core", name: "Core (20)" },
  { key: "mentor_pro", name: "Pro (75)" },
  { key: "mentor_scale", name: "Scale (250)" },
];

const DURATION_PRESETS = [
  { value: 30, label: "30 dias" },
  { value: 60, label: "60 dias" },
  { value: 365, label: "1 ano" },
  { value: 730, label: "2 anos" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "—"; }
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
    active: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    blocked: "bg-red-500/20 text-red-300 border-red-500/40",
    rejected: "bg-red-500/20 text-red-300 border-red-500/40",
    archived: "bg-gray-500/20 text-gray-300 border-gray-500/40",
  };
  const labels = {
    pending: "Pendente",
    active: "Ativo",
    blocked: "Bloqueado",
    rejected: "Rejeitado",
    archived: "Removido",
  };
  const cls = map[status] || "bg-gray-500/20 text-gray-300 border-gray-500/40";
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${cls}`}>
      {labels[status] || status || "—"}
    </span>
  );
}

export default function MentorsManagementTab() {
  const [mentors, setMentors] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // Approval form state per mentor row
  const [approvalDraft, setApprovalDraft] = useState({}); // { [mentorId]: { plan_key, duration_days, is_lifetime } }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === "archived") {
        params.only_archived = true;
      } else if (filter) {
        params.status = filter;
      }
      const res = await api.get("/api/owner/mentors", { params });
      setMentors(res.data?.mentors || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[mentors] load failed", err?.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const draftFor = (mentorId) =>
    approvalDraft[mentorId] || { plan_key: "mentor_pro", duration_days: 30, is_lifetime: false };
  const updateDraft = (mentorId, patch) =>
    setApprovalDraft((prev) => ({ ...prev, [mentorId]: { ...draftFor(mentorId), ...patch } }));

  const runAction = async (mentorId, path, body) => {
    setBusyId(mentorId);
    try {
      await api.post(`/api/owner/mentors/${mentorId}/${path}`, body || {});
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Erro ao processar solicitação.");
    } finally {
      setBusyId(null);
    }
  };

  const approve = (m) => {
    const d = draftFor(m.id);
    runAction(m.id, "approve", {
      plan_key: d.plan_key,
      duration_days: d.is_lifetime ? null : d.duration_days,
      is_lifetime: !!d.is_lifetime,
    });
  };
  const changePlan = (m, planKey) => runAction(m.id, "change-plan", { plan_key: planKey });
  const renew = (m) => {
    const d = draftFor(m.id);
    runAction(m.id, "renew", {
      duration_days: d.is_lifetime ? null : d.duration_days,
      is_lifetime: !!d.is_lifetime,
    });
  };
  const block = (m) => runAction(m.id, "block");
  const unblock = (m) => runAction(m.id, "unblock");
  const reject = (m) => runAction(m.id, "reject");
  const remove = (m) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remover o mentor ${m.name || m.email}? O acesso administrativo será revogado. Histórico e alunos serão preservados.`)) return;
    runAction(m.id, "remove");
  };
  const restore = (m) => runAction(m.id, "restore");

  return (
    <div data-testid="mentors-management-tab" className="space-y-4">
      {/* Filter tabs */}
      <div className="flex items-center gap-1 flex-wrap">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            data-testid={`mentor-filter-${f.key || "all"}`}
            onClick={() => setFilter(f.key)}
            className={`text-[11px] font-bold px-3 py-1.5 rounded-lg border transition-colors ${
              filter === f.key
                ? "bg-[#FF6B00] text-black border-[#FF6B00]"
                : "bg-[#14161C] text-gray-300 border-[#282D3D] hover:border-[#FF6B00]/50"
            }`}
          >
            {f.label}
          </button>
        ))}
        <button
          onClick={load}
          className="ml-auto text-[11px] text-gray-300 hover:text-white flex items-center gap-1"
        >
          <RefreshCcw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      {loading && (
        <div className="text-center text-xs text-gray-400 py-6">Carregando mentores...</div>
      )}

      {!loading && mentors.length === 0 && (
        <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-6 text-center text-xs text-gray-400">
          Nenhum mentor encontrado com este filtro.
        </div>
      )}

      {!loading && mentors.map((m) => {
        const d = draftFor(m.id);
        const isPending = m.mentor_status === "pending";
        const isBlocked = m.mentor_status === "blocked";
        const isRejected = m.mentor_status === "rejected";
        const isArchived = m.mentor_status === "archived";
        const nearCapacity = m.capacity && m.usage_active_students / m.capacity >= 0.8;
        return (
          <div
            key={m.id}
            data-testid={`mentor-row-${m.id}`}
            className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4 space-y-3"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-bold text-white">{m.name || m.email}</h3>
                  <StatusBadge status={m.mentor_status || "pending"} />
                  {m.current_plan && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-[#FF6B00]/50 text-[#FF6B00]">
                      {m.plan?.name || m.current_plan}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-400">{m.email}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">
                  Cadastrado em {fmtDate(m.created_at)}
                  {m.plan_expires_at && ` • Vence em ${fmtDate(m.plan_expires_at)}`}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-[#1A1D26] border border-[#282D3D] rounded-lg px-3 py-1.5 text-center min-w-[100px]">
                  <div className="text-[10px] text-gray-400 flex items-center justify-center gap-1">
                    <Users className="w-3 h-3" /> Uso
                  </div>
                  <div className={`text-sm font-bold ${nearCapacity ? "text-yellow-300" : "text-white"}`}>
                    {m.usage_active_students ?? 0}
                    <span className="text-gray-400 font-normal"> / {m.capacity ?? "—"}</span>
                  </div>
                </div>
                <div className="bg-[#1A1D26] border border-[#282D3D] rounded-lg px-3 py-1.5 text-center">
                  <div className="text-[10px] text-gray-400">Plano solicitado</div>
                  <div className="text-sm font-bold text-white">
                    {m.requested_plan ? PLANS.find((p) => p.key === m.requested_plan)?.name || m.requested_plan : "—"}
                  </div>
                </div>
              </div>
            </div>

            {/* Action row: approve / change plan / renew */}
            {isArchived ? (
              <div className="bg-[#1A1D26] border border-[#282D3D] rounded-lg p-3 flex items-center justify-between">
                <div className="text-[11px] text-gray-400">
                  Mentor arquivado{m.archived_at ? ` em ${fmtDate(m.archived_at)}` : ""}. Histórico preservado.
                </div>
                <button
                  data-testid={`mentor-restore-${m.id}`}
                  onClick={() => restore(m)}
                  disabled={busyId === m.id}
                  className="py-1.5 px-3 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-bold text-[11px] flex items-center gap-1 disabled:opacity-60"
                >
                  <ShieldCheck className="w-3.5 h-3.5" /> Restaurar
                </button>
              </div>
            ) : (
            <div className="bg-[#1A1D26] border border-[#282D3D] rounded-lg p-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                <div>
                  <label className="block text-[10px] text-gray-400 mb-1">Plano</label>
                  <select
                    data-testid={`mentor-plan-select-${m.id}`}
                    value={d.plan_key}
                    onChange={(e) => updateDraft(m.id, { plan_key: e.target.value })}
                    className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-1.5 text-white focus:outline-none focus:border-[#FF6B00]"
                  >
                    {PLANS.map((p) => (
                      <option key={p.key} value={p.key}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-400 mb-1">Duração</label>
                  <select
                    data-testid={`mentor-duration-select-${m.id}`}
                    value={d.is_lifetime ? "lifetime" : String(d.duration_days)}
                    onChange={(e) => {
                      if (e.target.value === "lifetime") updateDraft(m.id, { is_lifetime: true });
                      else updateDraft(m.id, { is_lifetime: false, duration_days: parseInt(e.target.value, 10) });
                    }}
                    className="w-full bg-[#0F1218] border border-[#282D3D] rounded-lg px-2 py-1.5 text-white focus:outline-none focus:border-[#FF6B00]"
                  >
                    {DURATION_PRESETS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                    <option value="lifetime">Vitalício</option>
                  </select>
                </div>
                <div className="flex items-end gap-2">
                  {isPending ? (
                    <>
                      <button
                        data-testid={`mentor-approve-${m.id}`}
                        onClick={() => approve(m)}
                        disabled={busyId === m.id}
                        className="flex-1 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold text-[11px] uppercase tracking-wider flex items-center justify-center gap-1 disabled:opacity-60"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Aprovar
                      </button>
                      <button
                        data-testid={`mentor-reject-${m.id}`}
                        onClick={() => reject(m)}
                        disabled={busyId === m.id}
                        className="py-1.5 px-3 rounded-lg bg-[#1F2330] hover:bg-red-500/20 hover:text-red-300 border border-[#3E4760] text-gray-300 font-bold text-[11px] flex items-center gap-1 disabled:opacity-60"
                      >
                        <XCircle className="w-3.5 h-3.5" /> Rejeitar
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        data-testid={`mentor-renew-${m.id}`}
                        onClick={() => renew(m)}
                        disabled={busyId === m.id || isBlocked || isRejected}
                        className="flex-1 py-1.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-[11px] uppercase tracking-wider flex items-center justify-center gap-1 disabled:opacity-60"
                      >
                        <RefreshCcw className="w-3.5 h-3.5" /> Renovar
                      </button>
                      {isBlocked ? (
                        <button
                          data-testid={`mentor-unblock-${m.id}`}
                          onClick={() => unblock(m)}
                          disabled={busyId === m.id}
                          className="py-1.5 px-3 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-bold text-[11px] flex items-center gap-1"
                        >
                          <ShieldCheck className="w-3.5 h-3.5" /> Desbloquear
                        </button>
                      ) : (
                        <button
                          data-testid={`mentor-block-${m.id}`}
                          onClick={() => block(m)}
                          disabled={busyId === m.id}
                          className="py-1.5 px-3 rounded-lg bg-[#1F2330] hover:bg-red-500/20 hover:text-red-300 border border-[#3E4760] text-gray-300 font-bold text-[11px] flex items-center gap-1"
                        >
                          <ShieldOff className="w-3.5 h-3.5" /> Bloquear
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {!isPending && m.current_plan && d.plan_key !== m.current_plan && (
                <button
                  data-testid={`mentor-change-plan-${m.id}`}
                  onClick={() => changePlan(m, d.plan_key)}
                  disabled={busyId === m.id}
                  className="text-[11px] text-[#FF6B00] hover:text-[#FF8533] font-bold flex items-center gap-1"
                >
                  <MoreHorizontal className="w-3.5 h-3.5" /> Trocar plano para {PLANS.find((p) => p.key === d.plan_key)?.name}
                </button>
              )}

              {nearCapacity && !isPending && (
                <div className="flex items-center gap-1.5 text-[11px] text-yellow-300">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Mentor próximo do limite do plano ({m.usage_active_students} / {m.capacity}).
                </div>
              )}

              {/* Danger zone: remove mentor */}
              <div className="flex justify-end pt-1 border-t border-[#282D3D]">
                <button
                  data-testid={`mentor-remove-${m.id}`}
                  onClick={() => remove(m)}
                  disabled={busyId === m.id}
                  className="text-[11px] font-bold px-3 py-1 rounded bg-red-600/10 text-red-300 border border-red-600/40 hover:bg-red-600/20 disabled:opacity-60"
                >
                  Remover mentor
                </button>
              </div>
            </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
